import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { submitExitSurvey, hasSeenExitSurvey, markExitSurveySeen } from '@/utils/signupFunnel';

/**
 * Anonymous "what stopped you?" inline survey shown at high-drop-off
 * signup/onboarding steps. One-click answer — no textarea, no friction.
 *
 * Reasons map directly to product levers:
 *   price     → revisit Stripe / pricing
 *   trust     → strengthen trust copy + social proof
 *   confused  → simplify UI / add tooltips
 *   other     → catch-all (still measurable)
 *
 * Sticky-dismissed per (kind, step) per browser session via sessionStorage,
 * so the prompt never nags the same user twice.
 *
 * Props:
 *   - kind:  "signup" | "onboarding"
 *   - step:  step id this prompt is mounted on
 *   - delaySeconds: optional delay before showing (default 0 = inline)
 *   - className: optional extra classes
 */
const REASONS = [
  { id: 'price', emoji: '💰', label: 'Pricing' },
  { id: 'trust', emoji: '🔒', label: 'Trust' },
  { id: 'confused', emoji: '🤔', label: 'Confused' },
  { id: 'other', emoji: '💬', label: 'Other' },
];

export default function ExitSurveyPrompt({
  kind = 'signup',
  step,
  delaySeconds = 0,
  className = '',
}) {
  const [visible, setVisible] = useState(false);
  const [submittedReason, setSubmittedReason] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Honour the per-session dismiss flag — never show twice for the same step.
  const alreadySeen = step ? hasSeenExitSurvey(kind, step) : true;

  useEffect(() => {
    if (alreadySeen || dismissed || submittedReason) return;
    if (delaySeconds <= 0) {
      setVisible(true);
      return;
    }
    const t = setTimeout(() => setVisible(true), delaySeconds * 1000);
    return () => clearTimeout(t);
  }, [alreadySeen, delaySeconds, dismissed, submittedReason]);

  if (!step || alreadySeen) return null;
  if (!visible || dismissed) return null;

  const handleClick = (reasonId) => {
    submitExitSurvey(reasonId, { kind, fromStep: step });
    markExitSurveySeen(kind, step);
    setSubmittedReason(reasonId);
  };

  const handleDismiss = () => {
    markExitSurveySeen(kind, step);
    setDismissed(true);
  };

  if (submittedReason) {
    return (
      <div
        className={`rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs flex items-center gap-2 ${className}`}
        data-testid="exit-survey-thanks"
      >
        <Check className="w-3.5 h-3.5 text-emerald-700" />
        <span className="text-emerald-900">Thanks for the feedback! We'll use it to improve this step.</span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2.5 ${className}`}
      data-testid={`exit-survey-prompt-${step}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs text-slate-700">
          <span className="font-medium">Hesitating?</span> Tell us what's holding you back — anonymous & one-click.
        </p>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-700 p-0.5"
          aria-label="Dismiss survey"
          data-testid="exit-survey-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {REASONS.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => handleClick(r.id)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-900 transition-colors"
            data-testid={`exit-survey-reason-${r.id}`}
          >
            <span aria-hidden="true">{r.emoji}</span>
            <span>{r.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
